import {
  Alert,
  Autocomplete,
  Box,
  createFilterOptions,
  TextField,
} from '@mui/material'
import PassengerSelectRows from 'components/Common/DisplayValues/PassengerSelectRows'
import { maskPhoneNumber } from 'components/Common/Phone/displayPhone'
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm'
import ConnectForm from 'components/RouteWizard/ConnectForm'
import Input from 'components/RouteWizard/Input'
import useClientUsers from 'hooks/useClientUsers'
import React, { useEffect, useMemo, useState } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import 'yup-phone'
import { Passenger } from './AdditionalPassengerDetails'

const phoneValidator = yup.string().phone()
const isValidPhone = (num?: string | null): boolean => {
  if (!num) return false
  try {
    return phoneValidator.isValidSync(num)
  } catch {
    return false
  }
}

type MainPassengerProps = {
  clientId: number
  disabled?: boolean
  onPassengerChange: (passenger: Passenger) => void
}

const MainPassengerDetails = ({
  clientId,
  disabled,
  onPassengerChange,
}: MainPassengerProps) => {
  const currentUser = useSelector((state: any) => state.users.user)
  const filter = createFilterOptions<any>()

  const { setValue, control } = useFormContext()
  const {
    fieldState: { error },
  } = useController({
    name: `mainPassenger`,
    control,
  })

  const { users } = useClientUsers(clientId)

  const options = useMemo<Passenger[]>(
    () =>
      users
        ?.filter((users: any) => users.lastName !== undefined)
        .sort((a: any, b: any) => (a.firstName > b.firstName ? 1 : -1))
        .map((u: any) => ({
          name:
            u.name ||
            [u.firstName, u.lastName].filter(Boolean).join(' ') ||
            'NOT SET',
          email: u.email,
          isLeadPassenger: true,
          contactNumber: u.contactNumber || u.contact.mobileNumber,
          userId: u.id,
          userProperties: u.userProperties,
        })) || [],
    [users],
  )

  const name = useWatch({
    control,
    name: `mainPassenger.name`,
  })
  const [inputValue, setInputValue] = useState<string | null>(
    name === undefined ? null : name,
  )

  useEffect(() => {
    if (name === '' && inputValue !== '') {
      setInputValue('')
    }
  }, [name])

  useEffect(() => {
    if (currentUser.roles.includes('Traveller')) {
      setTimeout(() => {
        setInputValue(`${currentUser.firstName} ${currentUser.lastName}`)

        setValue('mainPassenger', {
          isLeadPassenger: true,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          userId: currentUser.userId,
          contactNumber: currentUser.contact.mobileNumber,
          email: currentUser.email,
          requiresContactNumber: false,
          requiresEmail: false,
        })
      }, 800)
    }
  }, [currentUser])

  const requiresContactNumber = useWatch({
    control,
    name: `mainPassenger.requiresContactNumber`,
  })
  const requiresEmail = useWatch({
    control,
    name: `mainPassenger.requiresEmail`,
  })
  const selectedUserId = useWatch({
    control,
    name: `mainPassenger.userId`,
  })

  const pickedUserMissingContact =
    selectedUserId != null && requiresContactNumber
  const pickedUserMissingEmail = selectedUserId != null && requiresEmail
  const showMissingDetailsNotice =
    pickedUserMissingContact || pickedUserMissingEmail

  const missingDetailsMessage = (() => {
    if (pickedUserMissingContact && pickedUserMissingEmail) {
      return "This passenger's profile is missing a valid mobile number and email address. Please enter a valid mobile number below (email is optional)."
    }
    if (pickedUserMissingContact) {
      return "This passenger's profile is missing a valid mobile number. Please enter one below to continue."
    }
    return "This passenger's profile is missing an email address. You can add one below (optional)."
  })()

  if (clientId == null) {
    return null
  }

  const displayString = (option: Passenger) => {
    const name = option.name
    const email = option.email
      ? option.email
      : option.contactNumber
      ? maskPhoneNumber(option.contactNumber)
      : ''

    return PassengerSelectRows({
      main: name,
      description: email,
    })
  }

  return (
    <ConnectForm>
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          flexGrow: 1,
        }}
      >
        <Autocomplete
          value={name || null}
          options={options}
          disabled={disabled || false}
          getOptionLabel={(user) => {
            if (user == null) {
              return ''
            }

            if (typeof user === 'string') {
              return user
            }

            if (user.inputValue) {
              return user.inputValue
            }

            return user.name
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params)

            const { inputValue } = params
            // Suggest the creation of a new value
            const isExisting = options.some(
              (option) => inputValue === option.name,
            )
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                inputValue,
                name: `Add "${inputValue}"`,
              })
            }

            return filtered
          }}
          freeSolo
          autoSelect
          id={`mainPassenger-select`}
          onChange={(event, newValue) => {
            console.log(`newValue`, newValue)
            if (newValue && newValue.inputValue) {
              setValue('mainPassenger', {
                isLeadPassenger: true,
                name: newValue.inputValue.trim(),
                userId: undefined,
                requiresContactNumber: true,
                requiresEmail: true,
              })
              setInputValue(newValue.inputValue)
            } else if (typeof newValue === 'string') {
              setValue('mainPassenger', {
                isLeadPassenger: true,
                name: newValue.trim(),
                userId: undefined,
                requiresContactNumber: true,
                requiresEmail: true,
              })
              setInputValue(newValue)
            } else if (newValue == null) {
              setValue('mainPassenger', {
                isLeadPassenger: true,
                name: undefined,
                userId: undefined,
                requiresContactNumber: false,
                requiresEmail: false,
              })
              setInputValue(null)
            } else {
              const name = newValue?.name
                ? newValue.name
                : [newValue.firstName, newValue.lastName]
                    .filter((n) => n != null)
                    .join(' ')
              const rawContactNumber =
                newValue?.contactNumber != null ? newValue.contactNumber : null
              const contactNumberValid = isValidPhone(rawContactNumber)
              setValue('mainPassenger', {
                ...newValue,
                contactNumber: contactNumberValid ? rawContactNumber : null,
                requiresContactNumber: !contactNumberValid,
                email: newValue?.email != null ? newValue.email : null,
                requiresEmail: newValue?.email == null,
                name,
              } as Passenger)
              onPassengerChange(newValue as Passenger)
              setInputValue(name)
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.userId}>
              {displayString(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                value: inputValue || '',
                onChange: (e: React.BaseSyntheticEvent) => {
                  setInputValue(e.target.value)
                  params?.inputProps?.onChange(e)
                },
              }}
              error={Boolean(error?.message || error?.name?.message)}
              helperText={error?.message || error?.name?.message}
              label={'Lead Passenger Name'}
            />
          )}
          sx={{ flex: '1' }}
        />
        {showMissingDetailsNotice && (
          <Alert severity="info">{missingDetailsMessage}</Alert>
        )}
        {requiresContactNumber && (
          <MuiPhoneForm
            label="Contact number"
            name={`mainPassenger.contactNumber`}
            fullWidth
          />
        )}
        {requiresEmail && <Input label="Email" name={`mainPassenger.email`} />}
      </Box>
    </ConnectForm>
  )
}

export default MainPassengerDetails
